import { useLanguage, useUser } from '@/hooks/Contexts'
import { API_URL } from '@/utils/Consts'
import { formatWithGBP } from '@/utils/FloatNumber'
import { Format } from '@/utils/Format'
import { svgs } from '@/utils/Images'
import { Address, ConsolationReward, Order, OrderItem } from '@models/Index'
import { appUtils } from 'lib/AppUtils'
import { useRouter } from 'next/router'
import { FC, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { useSWRConfig } from 'swr'
import InstantWinTicket from './Competition/InstantWinTicket'
import { CountDown } from './CountDown'
import { ExpandableBlock } from './FormElement'
import { ImgFallback } from './Image'
import TotalLayout from './Layouts/TotalLayout'
import { IframeModal } from './Modal'

export const OrderDetail: FC<{ order: Order }> = ({ order }) => {
  const { payment: payStr, orders: orderStr } = useLanguage()
  const user = useUser()
  return (
    <ExpandableBlock
      title={orderStr.order_details}
      titleTxtCls='text-cyan-v2'
      // @ts-ignore
      togglerProps={{ 'data-testid': 'div-orderCplt-orderDtl' }}
      openDefault
      innerCls='border-t border-cyan-v2 pt-4'
    >
      <div className='flex items-center justify-center gap-x-2 text-lg font-semibold md:text-2xl'>
        <svg className='mr-1 h-8 w-8 text-cyan-v2' viewBox='0 0 17 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
          {svgs.check_solid}
        </svg>
        {orderStr.thank_you_for_order}
      </div>
      <p className='mt-2 text-center font-medium text-neutral-600'>{orderStr.will_email_confirmation}</p>
      <div className='mt-4 flex flex-wrap items-center justify-center gap-4 whitespace-nowrap border-t border-cyan-v2 pt-4 text-xs font-medium md:text-sm'>
        {[
          { name: orderStr.order_number, val: order.id },
          { name: orderStr.date, val: Format.dateHourMinuteFormat(order.created_at) },
          { name: orderStr.total, val: formatWithGBP(order.grand_total) },
          { name: payStr.payment, val: order.payment_method?.includes?.('wallet') ? payStr.wallet : payStr[order.payment_method] },
          { name: orderStr.email, val: order.email ?? user.email },
        ].map((item, index) => (
          <div key={index} className='flex items-center'>
            <span className='capitalize text-neutral-600'>{item.name}:</span>
            <span className='ml-2 text-black'>{item.val}</span>
          </div>
        ))}
      </div>
    </ExpandableBlock>
  )
}

export const OrderItemLayout: FC<{ orderItem: OrderItem; order: Order }> = ({ orderItem, order }) => {
  const { orders: orderStr } = useLanguage()
  const [showModal, setShowModal] = useState(false)
  const [modalIFrame, setModalIFrame] = useState('')
  const [url, setUrl] = useState('')

  const valuePairs = useMemo(
    () => [
      orderItem?.answer ? { key: orderStr.chosen_answer, value: orderItem?.answer } : null,
      {
        key: orderStr.ticket_numbers,
        value: order.status > 1 && !!orderItem?.ticket_numbers ? orderItem.ticket_numbers.replace(/,/g, ', ') : '',
        cls: 'font-semibold text-error',
        testid: 'txt-order-ticketsNum',
      },
      orderItem?.consolation_rewards?.length > 0
        ? {
          key: orderStr.instant_win_tickets,
          value: orderItem.consolation_rewards
            ?.map(v => v.ticket_number)
            .sort((a, b) => a - b)
            .join(', '),
          cls: 'font-semibold text-success',
          testid: 'txt-order-instantTicketsNum',
        }
        : null,
      { key: orderStr.price, value: Format.withCurrency(orderItem?.price) },
      { key: orderStr.row_total, value: Format.withCurrency(orderItem?.row_total) },
    ],
    [order.status, orderItem, orderStr]
  )

  return (
    <div className='mb-4 flex flex-col border-b border-cyan-v2 pb-4'>
      <div className='mb-2 flex justify-between text-sm font-semibold md:hidden'>
        <span>{orderItem?.title}</span>
        <span className='ml-6 self-end text-base text-black md:text-lg'>x{orderItem?.qty}</span>
      </div>
      <div className='flex flex-row md:mt-0'>
        <ImgFallback
          src={orderItem?.thumbnail}
          alt={orderItem?.title ?? orderItem?.thumbnail}
          className='mr-2 max-h-24 w-24 self-baseline rounded object-contain md:mr-4 md:block md:max-h-36 md:w-48'
        />
        <div className='flex flex-1 flex-col justify-between text-xs md:text-sm'>
          <div className='hidden justify-between font-semibold md:flex'>
            <span>{orderItem?.title}</span>
            <span className='ml-4 self-end text-black md:ml-8'>x{orderItem?.qty}</span>
          </div>

          <div className='flex flex-col md:mt-1'>
            {valuePairs.map((item, index) =>
              item ? (
                <div key={index} className='flex justify-start last:font-semibold md:justify-between'>
                  <span className='mr-1 text-neutral-600'>{item.key}:</span>
                  <span className={`flex-1 break-words text-right ${item.cls || ''}`} data-testid={item.testid}>
                    {item.value}
                  </span>
                </div>
              ) : null
            )}
          </div>

          {appUtils.isEmpty(orderItem?.winner) ? null : (
            <div
              onClick={() => {
                setShowModal(true)
                setUrl(orderItem?.video_draw?.url ?? '')
                setModalIFrame(orderItem?.video_draw?.iframe ?? '')
              }}
              className={`flex justify-between font-semibold text-cyan-v2 underline ${appUtils.isEmpty(orderItem?.video_draw) ? '' : 'cursor-pointer'}`}
            >
              <span>{orderStr.winner_no}</span>
              <span>{orderItem?.winner}</span>
            </div>
          )}
        </div>
      </div>
      {orderItem?.video_draw && <IframeModal show={showModal} onClose={() => setShowModal(false)} url={url} iframe={modalIFrame} />}
    </div>
  )
}

export const OrderItems: FC<{ orderInfo: Order; showPayment?: boolean }> = ({ orderInfo, showPayment }) => {
  const { orders: orderStr, wallet: walletStr, payment: payStr } = useLanguage()

  const totalLayoutCls = 'mb-1 flex justify-between text-xs last:mb-0 md:ml-52 md:text-sm'

  const useDiscount = !appUtils.isEmpty(orderInfo.discount_rules)
  const useCredit = Number(orderInfo?.use_balance_amount) > 0
  const useCash = Number(orderInfo?.use_wallet_amount) > 0
  const useCoupon = Number(orderInfo?.coupon_amount) > 0

  return (
    <ExpandableBlock
      title={
        <>
          <span className='font-semibold text-cyan-v2'>{orderStr.order_items}</span>
          <span className='ml-2 mr-auto text-xs text-stone-600'>{'(' + appUtils.getItemsStr(orderInfo?.items.length, orderStr) + ')'}</span>
        </>
      }
      openDefault={true}
      innerCls='border-t border-cyan-v2 pt-4'
      outerCls='mt-4'
    >
      {orderInfo.items?.length > 0 ? orderInfo.items.map((item, index) => <OrderItemLayout orderItem={item} key={index} order={orderInfo} />) : null}

      {showPayment && (
        <div className={totalLayoutCls + ' font-semibold'}>
          <span>{payStr.payment_method}</span>
          <span>{orderInfo.payment_method?.includes('wallet') ? payStr.wallet : payStr[orderInfo.payment_method] || orderInfo.payment_method}</span>
        </div>
      )}

      {useCredit || useCash || useCoupon || useDiscount ? (
        <div className={totalLayoutCls + ' font-normal'}>
          <span>{payStr.subtotal}</span>
          <span>{formatWithGBP(orderInfo.subtotal)}</span>
        </div>
      ) : null}

      {useCash ? (
        <TotalLayout name={walletStr.via_cash} value={orderInfo.use_wallet_amount} isDiscount={true} outerCls={totalLayoutCls + ' font-normal'} />
      ) : null}

      {useCredit ? (
        <TotalLayout name={walletStr.via_credit} value={orderInfo.use_balance_amount} isDiscount={true} outerCls={totalLayoutCls + ' font-normal'} />
      ) : null}

      {useCoupon ? (
        <TotalLayout name={orderInfo.coupon_name} value={orderInfo.coupon_amount} isDiscount={true} outerCls={totalLayoutCls + ' font-normal'} />
      ) : null}

      {useDiscount &&
        orderInfo.discount_rules.map((item, index) => (
          <TotalLayout key={index} name={item.name} value={item.amount} isDiscount={true} outerCls={totalLayoutCls + ' font-normal'} />
        ))}

      <div className={totalLayoutCls + ' font-semibold'}>
        <span>{payStr.grand_total}</span>
        <span>{formatWithGBP(orderInfo.grand_total)}</span>
      </div>
    </ExpandableBlock>
  )
}

interface OrderConsolationProps {
  items: Array<OrderItem>
  showDescr?: boolean
}
export const OrderConsolation = (props: OrderConsolationProps) => {
  const hasInstantWinCompetition = useMemo<boolean>(() => {
    let bool = false
    props.items?.forEach(item => {
      if (!bool && item.consolation_numbers?.length > 0) {
        bool = true
      }
      return
    })
    return bool
  }, [props.items])
  return hasInstantWinCompetition ? <OrderConsolationContent {...props} /> : null
}
const OrderConsolationContent = ({ items, showDescr }: OrderConsolationProps) => {
  const { orders: orderStr } = useLanguage()
  const user = useUser()
  const [rewards, setRewards] = useState<Array<ConsolationReward>>([])
  const [types, setTypes] = useState<Array<string>>([])
  useEffect(() => {
    const arr = []
    const typesArr = []
    items?.forEach(item => {
      if (item.consolation_rewards?.length > 0) {
        item.consolation_rewards.forEach(reward => {
          arr.push(reward)
          if (reward['product_id'] && !typesArr.includes('product')) {
            typesArr.push('product')
          } else if (reward['alternative_cash_amount'] !== '0.00' && !typesArr.includes('cash')) {
            typesArr.push('cash')
          } else if (reward['alternative_credit_amount'] !== '0.00' && !typesArr.includes('credit')) {
            typesArr.push('credit')
          }
        })
      }
    })
    setRewards(arr)
    setTypes(typesArr)
  }, [items])
  return (
    <ExpandableBlock
      title={orderStr.instant_win_prize}
      titleTxtCls='text-cyan-v2'
      // @ts-ignore
      togglerProps={{ 'data-testid': 'div-orderCplt-istWin' }}
      openDefault={true}
      outerCls='mt-4'
      innerCls='border-t border-cyan-v2 pt-4'
    >
      <div className='ml-2 flex items-center justify-center gap-x-2 text-lg font-semibold md:text-2xl'>
        {rewards.length > 0 ? (
          <img src='/assets/party_popper.png' className='h-8 w-8' />
        ) : (
          <span className='h-8 w-8 text-neutral-600'>{svgs.frowning_face}</span>
        )}
        {orderStr.notify_prize[rewards.length > 1 ? 2 : rewards.length]}
        {rewards.length > 0 ? (
          <img src='/assets/party_popper.png' className='h-8 w-8 -scale-x-100' />
        ) : (
          <span className='h-8 w-8 text-neutral-600'>{svgs.frowning_face}</span>
        )}
      </div>
      {rewards.length > 0 ? (
        <div className='my-2 grid w-full grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6' data-testid='div-orderCplt-prizLst'>
          {rewards.map((v, i) => (
            <InstantWinTicket key={i} ticket={v} extraCls={rewards.length == 1 ? 'col-span-full w-1/2 sm:w-1/3 md:w-1/4 lg:w-1/6 mx-auto' : ''} />
          ))}
        </div>
      ) : (
        <div className='relative mt-2 h-32 w-full'>
          <img src='/assets/no_win_ticket.svg' className='pointer-events-none absolute inset-0 mx-auto h-full w-auto' />
          <div className='absolute inset-0 mx-auto mb-4 flex items-center justify-center text-center text-3xl font-bold'>NO WIN</div>
        </div>
      )}
      {rewards.length <= 0 ? (
        <p className='text-center font-medium text-neutral-600'>{orderStr.notify_prize_descr[7].templated(user?.profile?.first_name)}</p>
      ) : showDescr ? (
        <p className='text-center font-medium'>
          {orderStr.notify_prize_descr[rewards.length > 1 ? 1 : 0].templated(user?.profile?.first_name)}
          {types.includes('credit') ? orderStr.notify_prize_descr[4] : null}
          {types.includes('cash') ? orderStr.notify_prize_descr[5] : null}
          {types.includes('product') ? orderStr.notify_prize_descr[6] : null}
        </p>
      ) : null}
    </ExpandableBlock>
  )
}

export const OrderAddress: FC<{ address: Address }> = ({ address }) => {
  const { user: userStr } = useLanguage()
  const user = useUser()
  return (
    <ExpandableBlock
      title={userStr.billing_address}
      titleTxtCls='text-cyan-v2'
      openDefault={true}
      outerCls='mt-4'
      innerCls='flex flex-wrap whitespace-nowrap items-center border-t border-cyan-v2 pt-4 gap-4 text-xs font-medium md:text-sm'
    >
      {[
        {
          icon: svgs.user_solid,
          text: address?.first_name && address?.last_name ? address.first_name + ' ' + address.last_name : user.name,
        },
        { icon: svgs.phone, text: address?.phone },
        { icon: svgs.email, text: address?.email ?? user.email },
        { icon: svgs.map_pin, text: appUtils.addressFormat(address) },
      ].map((item, index) => (
        <div key={index} className='flex items-center'>
          <span className='h-4 w-4 text-cyan-v2'>{item.icon}</span>
          <span className='ml-2'>{item.text}</span>
        </div>
      ))}
    </ExpandableBlock>
  )
}

export const OrderCountDown: FC<{ expiredAt: string; extraCls?: string }> = ({ expiredAt, extraCls }) => {
  const { cart: cartStr } = useLanguage()
  const router = useRouter()
  const { mutate } = useSWRConfig()
  return (
    <div className={`text-red -mt-2 flex w-full flex-col items-center justify-center rounded p-2 text-sm md:mb-2 md:flex-row md:text-base ${extraCls || ''}`}>
      <div className='mr-2 flex items-center'>
        <svg className='mr-1 h-4 w-4' viewBox='0 0 20 20' fill='currentColor'>
          {svgs.clock}
        </svg>
        {cartStr.order_reserved}
      </div>
      <CountDown
        date={expiredAt}
        ms={true}
        extraCls='text-sm md:text-base font-semibold'
        onFinish={() => {
          mutate(API_URL.cartCount)
          router.push('/competitions')
          toast.warning(cartStr.order_canceled, { toastId: 'order-canceled' })
        }}
      />
    </div>
  )
}
